import React, { useState, useRef } from 'react';
import './UploadVideoModal.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlay, faVolumeHigh, faVolumeXmark, faVideo, faTimes, faFileVideo, faUpload, faCheck} from '@fortawesome/free-solid-svg-icons';

const UploadVideoModal = ({
    onClose,
    onUpload,
    uploading,
    uploadProgress,
    processingProgress,
    uploadStatus
}) => {
    const [selectedFile, setSelectedFile] = useState(null);
    const [videoPreview, setVideoPreview] = useState(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [isMuted, setIsMuted] = useState(false); // Por defecto CON audio
    const [isProcessing, setIsProcessing] = useState(false);
    const [processingStep, setProcessingStep] = useState('');
    const videoRef = useRef(null);

    const handleFileSelect = (event) => {
        const file = event.target.files[0];
        if (!file) return;

        // Validar que sea un video
        if (!file.type.startsWith('video/')) {
            alert('⚠️ Por favor selecciona un archivo de video válido');
            return;
        }

        // Validar tamaño según estrategia de procesamiento
        const fileSizeMB = file.size / (1024 * 1024);
        const maxSize = fileSizeMB > 10 ? 200 : 100; // Más espacio para videos grandes
        
        if (fileSizeMB > maxSize) {
            alert(`⚠️ El archivo es demasiado grande. Máximo ${maxSize}MB.\n\n💡 Para videos grandes se usará procesamiento optimizado.`);
            return;
        }

        setSelectedFile(file);
        const url = URL.createObjectURL(file);
        setVideoPreview(url);

        // Obtener dimensiones del video
        const video = document.createElement('video');
        video.src = url;
        video.onloadedmetadata = () => {
            // Video metadata loaded
        };
    };



    const handlePlayPause = () => {
        if (videoRef.current) {
            if (isPlaying) {
                videoRef.current.pause();
                setIsPlaying(false);
            } else {
                videoRef.current.play();
                setIsPlaying(true);
            }
        }
    };

    const handleMuteToggle = () => {
        if (videoRef.current) {
            setIsMuted(!isMuted);
            videoRef.current.muted = !isMuted;
        }
    };



    // Función simplificada - Solo sube el archivo original
    const prepareVideoForUpload = async (file) => {
        return new Promise((resolve) => {
            setProcessingStep('Preparando video...');
            
            // Simular preparación breve
            setTimeout(() => {
                setProcessingStep('¡Listo para subir!');
                resolve(file); // Siempre usar archivo original
            }, 1000);
        });
    };

    const processAndUpload = async () => {
        if (!selectedFile) return;

        try {
            setIsProcessing(true);
            setProcessingStep('Preparando video...');

            // Preparar archivo original para upload
            const processingPromise = prepareVideoForUpload(selectedFile);

            // Race entre procesamiento y timeout
            const timeoutPromise = new Promise((resolve) => {
                setTimeout(() => {
                    resolve(selectedFile);
                }, 15000); // 15 segundos máximo total
            });

            const processedFile = await Promise.race([processingPromise, timeoutPromise]);
            
            setProcessingStep('¡Listo! Iniciando subida...');

            // Pequeña pausa para que el usuario vea el mensaje
            await new Promise(resolve => setTimeout(resolve, 500));

            // Subir el video
            await onUpload(processedFile);

        } catch (error) {
            setProcessingStep('Error, subiendo archivo original...');
            
            try {
                // Fallback: subir original si hay error en procesamiento
                await onUpload(selectedFile);
            } catch (uploadError) {
                alert('Error al subir el video. Por favor, inténtalo de nuevo.');
            }
        } finally {
            setIsProcessing(false);
            setProcessingStep('');
        }
    };

    return (
        <div className="reels-upload-modal">
            <div className="reels-upload-content">
                {/* Header - Solo botón de cerrar */}
                <div className="reels-upload-header">
                    <button className="reels-btn-close" onClick={onClose}>
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>

                {/* Contenido del modal */}
                {!selectedFile ? (
                    // Selector de archivo
                    <div className="reels-file-selector">
                        <div className="reels-upload-zone">
                            <div className="reels-upload-icon-large">
                                <FontAwesomeIcon icon={faVideo} />
                            </div>
                            <h4>Selecciona un video</h4>
                            <p>Máximo 60 segundos</p>

                            <input
                                type="file"
                                accept="video/mp4,video/webm,video/quicktime,video/avi"
                                onChange={handleFileSelect}
                                style={{ display: 'none' }}
                                id="reels-video-input"
                            />
                            <label htmlFor="reels-video-input" className="reels-select-btn">
                                Elegir video
                            </label>
                        </div>
                    </div>
                ) : (uploadStatus === 'idle' && !uploading && !isProcessing) ? (
                    // Preview y edición
                    <div className="reels-video-editor">
                        <div className="reels-preview-container">
                            <div 
                                className="reels-video-preview" 
                                style={{ aspectRatio: '9/16' }}
                            >
                                {videoPreview && (
                                    <>
                                        <video
                                            ref={videoRef}
                                            src={videoPreview}
                                            muted={isMuted}
                                            loop
                                            style={{
                                                width: '100%',
                                                height: '100%',
                                                objectFit: 'cover' // Siempre cover para formato 9:16 perfecto
                                            }}
                                        />
                                        
                                        {/* Botón de play/pause superpuesto */}
                                        {!isPlaying && (
                                            <div className="reels-play-overlay" onClick={handlePlayPause}>
                                                <div className="reels-play-btn">
                                                    <FontAwesomeIcon icon={faPlay} />
                                                </div>
                                            </div>
                                        )}
                                        
                                        {/* Control táctil para pausar */}
                                        {isPlaying && (
                                            <div className="reels-video-touch-control" onClick={handlePlayPause}></div>
                                        )}

                                        {/* Botón de mute/unmute */}
                                        <div className="reels-mute-btn" onClick={handleMuteToggle}>
                                            <FontAwesomeIcon icon={isMuted ? faVolumeXmark : faVolumeHigh} />
                                        </div>
                                    </>
                                )}

                                {/* Fondo blur con logo siempre visible */}
                                <div className="reels-blur-background">
                                    <img
                                        src="/logo192.png"
                                        alt="Background blur"
                                        className="reels-blur-logo"
                                    />
                                </div>
                                

                            </div>
                        </div>

                        <div className="reels-editor-controls">
                            <div className="reels-upload-actions">
                                <button
                                    className="reels-btn-secondary"
                                    onClick={() => setSelectedFile(null)}
                                >
                                    Cambiar video
                                </button>
                                <button
                                    className="reels-btn-primary"
                                    onClick={processAndUpload}
                                    disabled={uploading || isProcessing}
                                >
                                    {isProcessing ? 'Optimizando video...' : uploading ? 'Subiendo a servidor...' : 'Subir historia'}
                                </button>
                            </div>

                            {/* Indicador de procesamiento */}
                            {isProcessing && (
                                <div className="reels-processing-overlay">
                                    <div className="reels-processing-content">
                                        <div className="reels-processing-spinner"></div>
                                        <h4>Optimizando video</h4>
                                        <p>{processingStep}</p>
                                        <small>Creando formato perfecto 9:16...</small>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                ) : isProcessing && uploadStatus === 'idle' ? (
                    // Procesamiento interno (antes de que inicie el upload)
                    <div className="reels-upload-status">
                        <div className="reels-progress-section">
                            <div className="reels-progress-icon">
                                <FontAwesomeIcon icon={faFileVideo} />
                            </div>
                            <h4>Optimizando video</h4>
                            <p>{processingStep}</p>
                            <div className="reels-spinner">
                                <div className="reels-processing-spinner"></div>
                            </div>
                        </div>
                    </div>
                ) : (
                    // Estado de upload/procesamiento del componente padre
                    <div className="reels-upload-status">
                        {uploadStatus === 'uploading' && (
                            <div className="reels-progress-section">
                                <div className="reels-progress-icon">
                                    <FontAwesomeIcon icon={faUpload} />
                                </div>
                                <h4>Subiendo historia...</h4>
                                <div className="reels-progress-bar">
                                    <div
                                        className="reels-progress-fill"
                                        style={{ width: `${uploadProgress || 0}%` }}
                                    ></div>
                                </div>
                                <span className="reels-progress-text">{Math.round(uploadProgress || 0)}%</span>
                            </div>
                        )}

                        {uploadStatus === 'processing' && (
                            <div className="reels-progress-section">
                                <div className="reels-progress-icon">
                                    <FontAwesomeIcon icon={faFileVideo} />
                                </div>
                                <h4>Procesando en servidor...</h4>
                                <div className="reels-progress-bar">
                                    <div
                                        className="reels-progress-fill reels-processing"
                                        style={{ width: `${processingProgress || 0}%` }}
                                    ></div>
                                </div>
                                <span className="reels-progress-text">{Math.round(processingProgress || 0)}%</span>
                            </div>
                        )}

                        {uploadStatus === 'completed' && (
                            <div className="reels-progress-section">
                                <div className="reels-progress-icon">
                                    <FontAwesomeIcon icon={faCheck} />
                                </div>
                                <h4>¡Historia subida exitosamente!</h4>
                                <p>Tu video ya está disponible para tus seguidores</p>
                            </div>
                        )}

                        {uploadStatus === 'error' && (
                            <div className="reels-progress-section reels-error">
                                <div className="reels-progress-icon">❌</div>
                                <h4>Error al subir video</h4>
                                <p>Hubo un problema. Intenta nuevamente.</p>
                                <button className="reels-btn-primary" onClick={onClose}>
                                    Cerrar
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default UploadVideoModal;
